import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useReactToPrint } from 'react-to-print';
import {
  FileText,
  Download,
  Calendar,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Printer,
  MapPin,
  Car,
  Package,
  List,
  Info,
  X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaxReportsPage = () => {
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [activeTab, setActiveTab] = useState('schedule-c');
  const [scheduleC, setScheduleC] = useState(null);
  const [quarterly, setQuarterly] = useState(null);
  const [incomeAudit, setIncomeAudit] = useState(null);
  const [njSummary, setNjSummary] = useState(null);
  const [companySettings, setCompanySettings] = useState(null);
  const [expandedLines, setExpandedLines] = useState({});
  const [expandedParts, setExpandedParts] = useState({ part_i: true, part_ii: true, part_iii: false, part_iv: false, part_v: true });
  const [showPrintModal, setShowPrintModal] = useState(false);
  const printRef = useRef(null);

  const years = [2024, 2025, 2026, 2027];

  useEffect(() => {
    fetchAllReports();
  }, [year]);

  const fetchAllReports = async () => {
    setLoading(true);
    try {
      const [scheduleCRes, quarterlyRes, incomeAuditRes, njSummaryRes, settingsRes] = await Promise.all([
        axios.get(`${API}/tax-reports/schedule-c/${year}`),
        axios.get(`${API}/tax-reports/quarterly/${year}`),
        axios.get(`${API}/tax-reports/income-audit/${year}`),
        axios.get(`${API}/tax-reports/nj-summary/${year}`),
        axios.get(`${API}/settings`),
      ]);
      setScheduleC(scheduleCRes.data);
      setQuarterly(quarterlyRes.data);
      setIncomeAudit(incomeAuditRes.data);
      setNjSummary(njSummaryRes.data);
      setCompanySettings(settingsRes.data);
    } catch (error) {
      console.error('Failed to fetch tax reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const toggleLineExpand = (line) => {
    setExpandedLines(prev => ({ ...prev, [line]: !prev[line] }));
  };

  const togglePartExpand = (part) => {
    setExpandedParts(prev => ({ ...prev, [part]: !prev[part] }));
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Schedule_C_${year}_${companySettings?.business_name || 'Business'}`,
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl text-white font-medium">Tax Reports</h1>
          <p className="text-gray-400">
            {companySettings?.business_type === 'single_member_llc' ? 'Single-Member LLC' : 'Business'} • 
            {companySettings?.state_of_formation || 'NJ'} • 
            {companySettings?.accounting_method === 'cash' ? 'Cash Basis' : 'Accrual Basis'}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value))}
            className="px-4 py-3 bg-[#1a1a1f] border border-white/10 rounded-xl text-white"
          >
            {years.map(y => (
              <option key={y} value={y}>Tax Year {y}</option>
            ))}
          </select>
          <button
            onClick={() => setShowPrintModal(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-xl transition-colors"
          >
            <Printer className="w-5 h-5" />
            Print Report
          </button>
        </div>
      </div>

      {/* Business Info Card */}
      <div className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-2xl border border-purple-500/20 p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Building className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-white font-medium">{companySettings?.company_name || 'Your Business'}</h2>
            <p className="text-gray-400 text-sm">
              EIN: {companySettings?.company_ein || 'Not Set'} • 
              Single-Member LLC • New Jersey
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-400">Formation Date</span>
            <p className="text-white">{companySettings?.date_of_formation || 'Not Set'}</p>
          </div>
          <div>
            <span className="text-gray-400">Accounting Method</span>
            <p className="text-white capitalize">{companySettings?.accounting_method || 'Cash'} Basis</p>
          </div>
          <div>
            <span className="text-gray-400">NJ Registration #</span>
            <p className="text-white">{companySettings?.nj_business_registration_number || 'Not Set'}</p>
          </div>
          <div>
            <span className="text-gray-400">Fiscal Year End</span>
            <p className="text-white capitalize">{companySettings?.fiscal_year_end || 'December'}</p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-white/10 pb-2 overflow-x-auto">
        {[
          { id: 'schedule-c', label: 'Schedule C', icon: FileText },
          { id: 'quarterly', label: 'Quarterly Estimates', icon: Calendar },
          { id: 'income-audit', label: 'Income Audit', icon: DollarSign },
          { id: 'nj-summary', label: 'NJ Summary', icon: MapPin },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-purple-600 text-white' 
                : 'text-gray-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Schedule C Tab */}
      {activeTab === 'schedule-c' && scheduleC && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <span className="text-gray-400 text-sm">Gross Receipts</span>
              </div>
              <div className="text-2xl text-green-400 font-bold">
                {formatCurrency(scheduleC.part_i_income?.line_1_gross_receipts)}
              </div>
              <p className="text-gray-500 text-xs mt-1">Line 1 • {scheduleC.invoice_count} invoices</p>
            </div>

            <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-red-500/20 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-400" />
                </div>
                <span className="text-gray-400 text-sm">Total Expenses</span>
              </div>
              <div className="text-2xl text-red-400 font-bold">
                {formatCurrency(scheduleC.line_28_total_expenses)}
              </div>
              <p className="text-gray-500 text-xs mt-1">Line 28 • {scheduleC.expense_count} expenses</p>
            </div>

            <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-2xl border border-purple-500/20 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-purple-400" />
                </div>
                <span className="text-gray-400 text-sm">Net Profit</span>
              </div>
              <div className={`text-2xl font-bold ${scheduleC.line_31_net_profit_or_loss >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(scheduleC.line_31_net_profit_or_loss)}
              </div>
              <p className="text-gray-500 text-xs mt-1">Line 31 • Flows to 1040</p>
            </div>

            <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-orange-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-400" />
                </div>
                <span className="text-gray-400 text-sm">Est. SE Tax</span>
              </div>
              <div className="text-2xl text-orange-400 font-bold">
                {formatCurrency(scheduleC.self_employment_tax_info?.estimated_se_tax)}
              </div>
              <p className="text-gray-500 text-xs mt-1">15.3% of 92.35%</p>
            </div>

            <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-xl bg-yellow-500/20 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-yellow-400" />
                </div>
                <span className="text-gray-400 text-sm">Sales Tax</span>
              </div>
              <div className="text-2xl text-yellow-400 font-bold">
                {formatCurrency(scheduleC.sales_tax_collected)}
              </div>
              <p className="text-gray-500 text-xs mt-1">Not income • Remit to NJ</p>
            </div>
          </div>

          {/* Part I - Income */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
            <button
              onClick={() => togglePartExpand('part_i')}
              className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-400" />
                Part I - Income
              </h3>
              {expandedParts.part_i ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedParts.part_i && (
              <div className="px-6 pb-6 space-y-3">
                {[
                  { line: '1', desc: 'Gross receipts or sales', value: scheduleC.part_i_income?.line_1_gross_receipts },
                  { line: '2', desc: 'Returns and allowances', value: scheduleC.part_i_income?.line_2_returns_allowances },
                  { line: '3', desc: 'Subtract line 2 from line 1', value: scheduleC.part_i_income?.line_3_net_receipts },
                  { line: '4', desc: 'Cost of goods sold (from line 42)', value: scheduleC.part_i_income?.line_4_cost_of_goods_sold },
                  { line: '5', desc: 'Gross profit. Subtract line 4 from line 3', value: scheduleC.part_i_income?.line_5_gross_profit },
                  { line: '6', desc: 'Other income', value: scheduleC.part_i_income?.line_6_other_income },
                  { line: '7', desc: 'Gross income. Add lines 5 and 6', value: scheduleC.part_i_income?.line_7_gross_income, highlight: true },
                ].map(item => (
                  <div key={item.line} className={`flex justify-between items-center py-2 px-4 rounded-lg ${item.highlight ? 'bg-green-500/10 border border-green-500/20' : 'bg-[#0a0a0b]'}`}>
                    <div>
                      <span className="text-purple-400 font-mono mr-2">Line {item.line}</span>
                      <span className="text-gray-300">{item.desc}</span>
                    </div>
                    <span className={`font-bold ${item.highlight ? 'text-green-400' : 'text-white'}`}>
                      {formatCurrency(item.value)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Part II - Expenses */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
            <button
              onClick={() => togglePartExpand('part_ii')}
              className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <TrendingDown className="w-5 h-5 text-red-400" />
                Part II - Expenses
              </h3>
              {expandedParts.part_ii ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedParts.part_ii && (
              <div className="px-6 pb-6">
                <div className="space-y-2">
                  {scheduleC.part_ii_expenses && Object.entries(scheduleC.part_ii_expenses).sort((a, b) => {
                    const lineA = parseInt(a[0].replace(/\D/g, '')) || 99;
                    const lineB = parseInt(b[0].replace(/\D/g, '')) || 99;
                    return lineA - lineB;
                  }).map(([line, data]) => (
                    <div key={line} className="bg-[#0a0a0b] rounded-lg overflow-hidden">
                      <button
                        onClick={() => toggleLineExpand(line)}
                        className="w-full flex justify-between items-center py-3 px-4 hover:bg-white/5 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {expandedLines[line] ? (
                            <ChevronDown className="w-4 h-4 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-500" />
                          )}
                          <span className="text-purple-400 font-mono">Line {line}</span>
                          <span className="text-gray-300">{data.description}</span>
                          <span className="text-gray-500 text-sm">({data.items?.length || 0} items)</span>
                        </div>
                        <span className="text-white font-bold">{formatCurrency(data.amount)}</span>
                      </button>
                      {expandedLines[line] && data.items && (
                        <div className="px-4 pb-3 space-y-1">
                          {data.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm py-1 px-4 bg-white/5 rounded">
                              <div>
                                <span className="text-gray-400">{item.date}</span>
                                <span className="text-gray-300 ml-2">{item.description}</span>
                                {item.vendor && <span className="text-gray-500 ml-2">• {item.vendor}</span>}
                              </div>
                              <div className="text-right">
                                {item.deductible_amount !== item.amount && (
                                  <span className="text-gray-500 line-through mr-2">{formatCurrency(item.amount)}</span>
                                )}
                                <span className="text-white">{formatCurrency(item.deductible_amount)}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Expense Summary Lines 28-32 */}
                <div className="mt-4 pt-4 border-t border-white/10 space-y-3">
                  {[
                    { line: '28', desc: 'Total expenses before expenses for business use of home', value: scheduleC.part_ii_summary?.line_28_total_expenses, color: 'text-red-400' },
                    { line: '29', desc: 'Tentative profit or (loss). Subtract line 28 from line 7', value: scheduleC.part_ii_summary?.line_29_tentative_profit },
                    { line: '30', desc: 'Expenses for business use of your home (Form 8829)', value: scheduleC.part_ii_summary?.line_30_home_office_deduction },
                    { line: '31', desc: 'Net profit or (loss). Subtract line 30 from line 29', value: scheduleC.part_ii_summary?.line_31_net_profit_or_loss, highlight: true },
                  ].map(item => (
                    <div key={item.line} className={`flex justify-between items-center py-3 px-4 rounded-lg ${item.highlight ? 'bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/20' : 'bg-[#0a0a0b]'}`}>
                      <div>
                        <span className="text-purple-400 font-mono mr-2">Line {item.line}</span>
                        <span className="text-gray-300">{item.desc}</span>
                      </div>
                      <span className={`font-bold text-xl ${item.highlight ? (item.value >= 0 ? 'text-green-400' : 'text-red-400') : (item.color || 'text-white')}`}>
                        {formatCurrency(item.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Part III - Cost of Goods Sold */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
            <button
              onClick={() => togglePartExpand('part_iii')}
              className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-400" />
                  Part III - Cost of Goods Sold
                </h3>
                {!scheduleC.part_iii_cogs?.applicable && (
                  <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">N/A for service business</span>
                )}
              </div>
              {expandedParts.part_iii ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedParts.part_iii && (
              <div className="px-6 pb-6">
                <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                    <p className="text-gray-300 text-sm">{scheduleC.part_iii_cogs?.note}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { line: '33', desc: 'Method(s) used to value closing inventory', value: 'N/A' },
                    { line: '34', desc: 'Was there any change in determining quantities, costs, or valuations?', value: 'No' },
                    { line: '35', desc: 'Inventory at beginning of year', value: scheduleC.part_iii_cogs?.line_35_beginning_inventory },
                    { line: '36', desc: 'Purchases less cost of items withdrawn for personal use', value: scheduleC.part_iii_cogs?.line_36_purchases },
                    { line: '37', desc: 'Cost of labor', value: scheduleC.part_iii_cogs?.line_37_labor_cost },
                    { line: '38', desc: 'Materials and supplies', value: scheduleC.part_iii_cogs?.line_38_materials_supplies },
                    { line: '39', desc: 'Other costs', value: scheduleC.part_iii_cogs?.line_39_other_costs },
                    { line: '40', desc: 'Add lines 35 through 39', value: scheduleC.part_iii_cogs?.line_40_total },
                    { line: '41', desc: 'Inventory at end of year', value: scheduleC.part_iii_cogs?.line_41_ending_inventory },
                    { line: '42', desc: 'Cost of goods sold. Subtract line 41 from line 40', value: scheduleC.part_iii_cogs?.line_42_cogs, highlight: true },
                  ].map(item => (
                    <div key={item.line} className={`flex justify-between items-center py-2 px-4 rounded-lg ${item.highlight ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-[#0a0a0b]'}`}>
                      <div>
                        <span className="text-purple-400 font-mono mr-2">Line {item.line}</span>
                        <span className="text-gray-300">{item.desc}</span>
                      </div>
                      <span className={`font-bold ${item.highlight ? 'text-blue-400' : 'text-gray-400'}`}>
                        {typeof item.value === 'number' ? formatCurrency(item.value) : item.value}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Part IV - Information on Your Vehicle */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
            <button
              onClick={() => togglePartExpand('part_iv')}
              className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <h3 className="text-white font-medium flex items-center gap-2">
                  <Car className="w-5 h-5 text-cyan-400" />
                  Part IV - Information on Your Vehicle
                </h3>
                {!scheduleC.part_iv_vehicle?.applicable && (
                  <span className="px-2 py-1 bg-gray-500/20 text-gray-400 text-xs rounded-full">No vehicle expenses claimed</span>
                )}
              </div>
              {expandedParts.part_iv ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedParts.part_iv && (
              <div className="px-6 pb-6">
                <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-cyan-400 mt-0.5" />
                    <p className="text-gray-300 text-sm">{scheduleC.part_iv_vehicle?.note}</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {[
                    { line: '43', desc: 'When did you place your vehicle in service for business purposes?', value: scheduleC.part_iv_vehicle?.line_43_date_placed_in_service || 'Not specified' },
                    { line: '44a', desc: 'Business miles', value: scheduleC.part_iv_vehicle?.line_44a_business_miles || 0 },
                    { line: '44b', desc: 'Commuting miles', value: scheduleC.part_iv_vehicle?.line_44b_commuting_miles || 0 },
                    { line: '44c', desc: 'Other miles', value: scheduleC.part_iv_vehicle?.line_44c_other_miles || 0 },
                    { line: '45', desc: 'Was your vehicle available for personal use during off-duty hours?', value: scheduleC.part_iv_vehicle?.line_45_available_personal_use ?? 'Not answered' },
                    { line: '46', desc: 'Do you (or your spouse) have another vehicle available for personal use?', value: scheduleC.part_iv_vehicle?.line_46_another_vehicle ?? 'Not answered' },
                    { line: '47a', desc: 'Do you have evidence to support your deduction?', value: scheduleC.part_iv_vehicle?.line_47a_evidence ?? 'Not answered' },
                    { line: '47b', desc: 'If Yes, is the evidence written?', value: scheduleC.part_iv_vehicle?.line_47b_evidence_written ?? 'Not answered' },
                  ].map(item => (
                    <div key={item.line} className="flex justify-between items-center py-2 px-4 rounded-lg bg-[#0a0a0b]">
                      <div>
                        <span className="text-purple-400 font-mono mr-2">Line {item.line}</span>
                        <span className="text-gray-300">{item.desc}</span>
                      </div>
                      <span className="font-bold text-gray-400">
                        {typeof item.value === 'number' ? item.value.toLocaleString() : String(item.value)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Part V - Other Expenses */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
            <button
              onClick={() => togglePartExpand('part_v')}
              className="w-full flex items-center justify-between p-6 hover:bg-white/5 transition-colors"
            >
              <h3 className="text-white font-medium flex items-center gap-2">
                <List className="w-5 h-5 text-yellow-400" />
                Part V - Other Expenses (Line 48)
              </h3>
              {expandedParts.part_v ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
            </button>
            {expandedParts.part_v && (
              <div className="px-6 pb-6">
                <p className="text-gray-400 text-sm mb-4">{scheduleC.part_v_other_expenses?.line_48_description}</p>
                
                {scheduleC.part_v_other_expenses?.line_48_items?.length > 0 ? (
                  <div className="space-y-2">
                    {scheduleC.part_v_other_expenses.line_48_items.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center py-2 px-4 rounded-lg bg-[#0a0a0b]">
                        <div>
                          <span className="text-gray-400 mr-2">{item.date}</span>
                          <span className="text-gray-300">{item.description}</span>
                          {item.vendor && <span className="text-gray-500 ml-2">• {item.vendor}</span>}
                        </div>
                        <span className="font-bold text-white">{formatCurrency(item.deductible_amount)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between items-center py-3 px-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 mt-4">
                      <span className="text-white font-medium">Line 48 Total - Enter on Line 27b</span>
                      <span className="font-bold text-yellow-400 text-xl">{formatCurrency(scheduleC.part_v_other_expenses?.line_48_total)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <List className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No other expenses recorded for this tax year</p>
                    <p className="text-sm mt-2">Categories that appear here: {scheduleC.part_v_other_expenses?.categories_included?.join(', ')}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quarterly Tab */}
      {activeTab === 'quarterly' && quarterly && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quarterly.quarters.map(q => (
              <div key={q.quarter} className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-purple-400 font-bold">Q{q.quarter}</span>
                  <span className="text-gray-500 text-sm">{q.period.split(' to ')[0]} - {q.period.split(' to ')[1]}</span>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Gross Income</span>
                    <span className="text-green-400">{formatCurrency(q.gross_income)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400 text-sm">Expenses</span>
                    <span className="text-red-400">{formatCurrency(q.total_expenses)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-white/10">
                    <span className="text-gray-400 text-sm">Net Profit</span>
                    <span className={q.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}>
                      {formatCurrency(q.net_profit)}
                    </span>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Est. SE Tax</span>
                    <span className="text-yellow-400 font-bold">{formatCurrency(q.estimated_se_tax)}</span>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Due: {q.estimated_tax_due_date}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Annual Totals */}
          <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 rounded-2xl border border-purple-500/20 p-6">
            <h3 className="text-white font-medium mb-4">Annual Totals</h3>
            <div className="grid grid-cols-3 gap-6">
              <div>
                <span className="text-gray-400 text-sm">Total Income</span>
                <p className="text-2xl text-green-400 font-bold">{formatCurrency(quarterly.annual_totals.total_income)}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Total Expenses</span>
                <p className="text-2xl text-red-400 font-bold">{formatCurrency(quarterly.annual_totals.total_expenses)}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Net Profit</span>
                <p className={`text-2xl font-bold ${quarterly.annual_totals.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(quarterly.annual_totals.net_profit)}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Income Audit Tab */}
      {activeTab === 'income-audit' && incomeAudit && (
        <div className="space-y-6">
          {/* Summary */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
              <span className="text-gray-400 text-sm">Total Invoiced in {year}</span>
              <p className="text-2xl text-white font-bold">{formatCurrency(incomeAudit.summary.total_invoiced)}</p>
              <p className="text-gray-500 text-xs">{incomeAudit.summary.invoice_count} invoices created</p>
            </div>
            <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
              <span className="text-gray-400 text-sm">Payment Received in {year}</span>
              <p className="text-2xl text-green-400 font-bold">{formatCurrency(incomeAudit.summary.total_received_in_year)}</p>
              <p className="text-gray-500 text-xs">{incomeAudit.summary.paid_count} paid (recognized as income)</p>
            </div>
            <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-5">
              <span className="text-gray-400 text-sm">Outstanding</span>
              <p className="text-2xl text-yellow-400 font-bold">{formatCurrency(incomeAudit.summary.total_outstanding)}</p>
              <p className="text-gray-500 text-xs">Not yet received</p>
            </div>
          </div>

          {/* Invoice List */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 overflow-hidden">
            <div className="p-4 border-b border-white/10">
              <h3 className="text-white font-medium">Invoice Payment Reconciliation - {year}</h3>
              <p className="text-gray-400 text-sm">Cash basis: Income recognized when payment received in {year}</p>
            </div>
            {incomeAudit.invoices.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#0a0a0b]">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Invoice #</th>
                      <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Client</th>
                      <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Invoice Date</th>
                      <th className="text-right px-4 py-3 text-gray-400 text-sm font-medium">Net Amount</th>
                      <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Status</th>
                      <th className="text-left px-4 py-3 text-gray-400 text-sm font-medium">Payment Date</th>
                      <th className="text-center px-4 py-3 text-gray-400 text-sm font-medium">Recognized in {year}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {incomeAudit.invoices.map((inv, idx) => (
                      <tr key={idx} className="border-t border-white/5 hover:bg-white/5">
                        <td className="px-4 py-3 text-white font-mono">{inv.invoice_number}</td>
                        <td className="px-4 py-3 text-gray-300">{inv.client_name}</td>
                        <td className="px-4 py-3 text-gray-400">{inv.invoice_date}</td>
                        <td className="px-4 py-3 text-white text-right font-medium">{formatCurrency(inv.net_amount)}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            inv.status === 'paid' ? 'bg-green-500/20 text-green-400' :
                            inv.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-gray-500/20 text-gray-400'
                          }`}>
                            {inv.status?.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-400">{inv.payment_received_date || '-'}</td>
                        <td className="px-4 py-3 text-center">
                          {inv.recognized_in_year ? (
                            <CheckCircle className="w-5 h-5 text-green-400 mx-auto" />
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No invoices found for {year}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* NJ Summary Tab */}
      {activeTab === 'nj-summary' && njSummary && (
        <div className="space-y-6">
          {/* Business Info */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
            <h3 className="text-white font-medium mb-4 flex items-center gap-2">
              <Building className="w-5 h-5 text-purple-400" />
              New Jersey Business Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <span className="text-gray-400 text-sm">Business Name</span>
                <p className="text-white">{njSummary.business_info.business_name || 'Not Set'}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Business Type</span>
                <p className="text-white capitalize">{njSummary.business_info.business_type?.replace(/_/g, ' ')}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">EIN</span>
                <p className="text-white">{njSummary.business_info.ein || 'Not Set'}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">NJ Registration #</span>
                <p className="text-white">{njSummary.business_info.nj_registration_number || 'Not Set'}</p>
              </div>
            </div>
          </div>

          {/* Income Summary */}
          <div className="bg-gradient-to-r from-green-900/30 to-blue-900/30 rounded-2xl border border-green-500/20 p-6">
            <h3 className="text-white font-medium mb-4">NJ Income Summary</h3>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400">NJ-Sourced Business Income</p>
                <p className="text-gray-400 text-sm">{njSummary.income_summary.flows_to}</p>
              </div>
              <p className={`text-3xl font-bold ${njSummary.income_summary.nj_sourced_income >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(njSummary.income_summary.nj_sourced_income)}
              </p>
            </div>
            <p className="text-gray-500 text-sm mt-4">{njSummary.income_summary.note}</p>
          </div>

          {/* Annual Report */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
            <h3 className="text-white font-medium mb-4">NJ Annual Report</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <span className="text-gray-400 text-sm">Due Date</span>
                <p className="text-white">{njSummary.annual_report.due_date || 'Based on formation date'}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Filing Fee</span>
                <p className="text-white">{formatCurrency(njSummary.annual_report.fee)}</p>
              </div>
              <div>
                <span className="text-gray-400 text-sm">Filing URL</span>
                <a href={njSummary.annual_report.filing_url} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300">
                  NJ Annual Reports Portal →
                </a>
              </div>
            </div>
          </div>

          {/* Tax Filing Checklist */}
          <div className="bg-[#1a1a1f] rounded-2xl border border-white/5 p-6">
            <h3 className="text-white font-medium mb-4">Tax Filing Checklist for {year}</h3>
            <div className="space-y-3">
              {[
                { task: 'Complete Schedule C with income and expenses', form: 'IRS Schedule C' },
                { task: 'Report net profit on Form 1040', form: 'IRS Form 1040 Schedule 1' },
                { task: 'Calculate and pay self-employment tax', form: 'IRS Schedule SE' },
                { task: 'Report business income on NJ-1040', form: 'NJ-1040 Line 17' },
                { task: 'File NJ Annual Report', form: 'NJ Division of Revenue' },
                njSummary.sales_tax?.total_collected > 0 && { task: 'Remit collected sales tax', form: 'NJ ST-50' },
              ].filter(Boolean).map((item, idx) => (
                <div key={idx} className="flex items-center gap-3 py-2 px-4 bg-[#0a0a0b] rounded-lg">
                  <input type="checkbox" className="w-5 h-5 rounded border-white/20 bg-transparent" />
                  <div className="flex-1">
                    <span className="text-gray-300">{item.task}</span>
                    <span className="text-purple-400 text-sm ml-2">({item.form})</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Important Notes */}
      <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-6 print:hidden">
        <h3 className="text-blue-400 font-medium mb-4">Important Tax Notes</h3>
        <ul className="space-y-2 text-sm text-gray-300">
          <li className="flex items-start gap-2">
            <span className="text-blue-400">•</span>
            <span><strong>Cash Basis:</strong> Income is recognized when payment is received, not when invoiced.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400">•</span>
            <span><strong>Meals:</strong> Only 50% of meal expenses are deductible per IRS rules.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400">•</span>
            <span><strong>Single-Member LLC:</strong> Income flows through to your personal tax return via Schedule C.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400">•</span>
            <span><strong>NJ Filing:</strong> NJ does not require a separate business return for single-member LLCs.</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-400">•</span>
            <span>This report is for informational purposes. Consult a tax professional for actual filing.</span>
          </li>
        </ul>
      </div>

      {/* Print Modal with Schedule C Report */}
      {showPrintModal && scheduleC && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShowPrintModal(false)} />
          <div className="relative w-full max-w-4xl bg-white rounded-2xl p-0 my-8 max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between z-10">
              <h2 className="text-xl text-gray-800 font-semibold">Schedule C Tax Report - {year}</h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handlePrint}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Print / Save PDF
                </button>
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Printable Schedule C Report */}
            <div ref={printRef} className="p-8" style={{ backgroundColor: 'white', fontFamily: 'Arial, sans-serif' }}>
              {/* IRS Header */}
              <div style={{ borderBottom: '3px solid #1e3a5f', paddingBottom: '16px', marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>SCHEDULE C (Form 1040)</p>
                    <p style={{ fontSize: '12px', color: '#666', margin: '2px 0' }}>Department of the Treasury - Internal Revenue Service</p>
                    <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e3a5f', margin: '8px 0 4px 0' }}>
                      Profit or Loss From Business
                    </h1>
                    <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>(Sole Proprietorship)</p>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontSize: '14px', fontWeight: 'bold', color: '#1e3a5f', margin: 0 }}>Tax Year {year}</p>
                    <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>
                      Generated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Business Information - IRS Schedule C Header */}
              <div style={{ border: '2px solid #1e3a5f', marginBottom: '20px' }}>
                {/* Row A-D */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ padding: '10px 12px', borderRight: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>A. Principal business or profession, including product or service</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      {companySettings?.principal_business || 'Not Set - Update in Settings'}
                    </p>
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>B. Business code (from IRS instructions)</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      {companySettings?.business_code || 'Not Set - Update in Settings'}
                    </p>
                  </div>
                </div>

                {/* Row C-D */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ padding: '10px 12px', borderRight: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>C. Business name</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      {companySettings?.company_name || 'Not Set - Update in Settings'}
                    </p>
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>D. Employer ID number (EIN)</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      {companySettings?.company_ein || 'Not Set - Update in Settings'}
                    </p>
                  </div>
                </div>

                {/* Row E - Address */}
                <div style={{ padding: '10px 12px', borderBottom: '1px solid #e2e8f0' }}>
                  <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>E. Business address (including suite or room no.)</p>
                  <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                    {companySettings?.company_address || 'Not Set - Update in Settings'}
                  </p>
                </div>

                {/* Row - City, State, ZIP */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ padding: '10px 12px', borderRight: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>City</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      {companySettings?.company_city || 'Not Set'}
                    </p>
                  </div>
                  <div style={{ padding: '10px 12px', borderRight: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>State</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      {companySettings?.company_state || 'Not Set'}
                    </p>
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>ZIP Code</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      {companySettings?.company_zip || 'Not Set'}
                    </p>
                  </div>
                </div>

                {/* Row F-G */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid #e2e8f0' }}>
                  <div style={{ padding: '10px 12px', borderRight: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>F. Accounting method</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      <span style={{ marginRight: '16px' }}>
                        {companySettings?.accounting_method === 'cash' ? '☑' : '☐'} Cash
                      </span>
                      <span style={{ marginRight: '16px' }}>
                        {companySettings?.accounting_method === 'accrual' ? '☑' : '☐'} Accrual
                      </span>
                      <span>☐ Other</span>
                    </p>
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>G. Did you "materially participate" in the operation of this business?</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      ☑ Yes  ☐ No
                    </p>
                  </div>
                </div>

                {/* Row H-I */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <div style={{ padding: '10px 12px', borderRight: '1px solid #e2e8f0' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>H. If you started or acquired this business during {year}, check here</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>☐</p>
                  </div>
                  <div style={{ padding: '10px 12px' }}>
                    <p style={{ fontSize: '10px', color: '#64748b', margin: '0 0 2px 0', fontWeight: '600' }}>I. Did you make any payments that require Form 1099?</p>
                    <p style={{ fontSize: '13px', color: '#1e293b', margin: 0, fontWeight: '500' }}>
                      ☐ Yes  ☑ No
                    </p>
                  </div>
                </div>
              </div>

              {/* Part I - Income */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#1e3a5f', color: 'white', padding: '10px 16px', borderRadius: '6px 6px 0 0' }}>
                  <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: 0 }}>Part I — Income</h2>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', width: '60px', color: '#64748b', fontWeight: '500' }}>1</td>
                      <td style={{ padding: '10px 0' }}>Gross receipts or sales</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#059669' }}>
                        {formatCurrency(scheduleC?.part_i_income?.line_1_gross_receipts)}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', color: '#64748b', fontWeight: '500' }}>2</td>
                      <td style={{ padding: '10px 0' }}>Returns and allowances</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(scheduleC?.part_i_income?.line_2_returns_allowances)}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', color: '#64748b', fontWeight: '500' }}>3</td>
                      <td style={{ padding: '10px 0' }}>Subtract line 2 from line 1</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(scheduleC?.part_i_income?.line_3_net_receipts)}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', color: '#64748b', fontWeight: '500' }}>4</td>
                      <td style={{ padding: '10px 0' }}>Cost of goods sold (from Part III, line 42)</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(scheduleC?.part_i_income?.line_4_cost_of_goods_sold)}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', color: '#64748b', fontWeight: '500' }}>5</td>
                      <td style={{ padding: '10px 0' }}>Gross profit (subtract line 4 from line 3)</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(scheduleC?.part_i_income?.line_5_gross_profit)}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', color: '#64748b', fontWeight: '500' }}>6</td>
                      <td style={{ padding: '10px 0' }}>Other income</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(scheduleC?.part_i_income?.line_6_other_income)}
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: '#f0fdf4', borderBottom: '2px solid #059669' }}>
                      <td style={{ padding: '12px 16px', color: '#059669', fontWeight: 'bold' }}>7</td>
                      <td style={{ padding: '12px 0', fontWeight: 'bold', color: '#059669' }}>Gross income (add lines 5 and 6)</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold', fontSize: '15px', color: '#059669' }}>
                        {formatCurrency(scheduleC?.part_i_income?.line_7_gross_income)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Part II - Expenses */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#1e3a5f', color: 'white', padding: '10px 16px', borderRadius: '6px 6px 0 0' }}>
                  <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: 0 }}>Part II — Expenses</h2>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <tbody>
                    {scheduleC?.part_ii_expenses && Object.entries(scheduleC.part_ii_expenses)
                      .filter(([_, data]) => data.amount > 0)
                      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                      .map(([lineNum, data]) => (
                        <tr key={lineNum} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '8px 16px', width: '80px', color: '#64748b', fontWeight: '500' }}>Line {lineNum}</td>
                          <td style={{ padding: '8px 0' }}>
                            {data.description}
                            {data.items && data.items.length > 1 && (
                              <span style={{ color: '#94a3b8', fontSize: '11px', marginLeft: '8px' }}>
                                ({data.items.length} items combined)
                              </span>
                            )}
                          </td>
                          <td style={{ padding: '8px 16px', textAlign: 'right', fontWeight: '500', color: '#dc2626' }}>
                            {formatCurrency(data.amount)}
                          </td>
                        </tr>
                      ))}
                    <tr style={{ backgroundColor: '#fef2f2', borderBottom: '2px solid #dc2626' }}>
                      <td style={{ padding: '12px 16px', color: '#dc2626', fontWeight: 'bold' }}>Line 28</td>
                      <td style={{ padding: '12px 0', fontWeight: 'bold', color: '#dc2626' }}>Total expenses (add lines 8 through 27)</td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold', fontSize: '15px', color: '#dc2626' }}>
                        {formatCurrency(scheduleC?.line_28_total_expenses)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Part V - Net Profit */}
              <div style={{ marginBottom: '24px' }}>
                <div style={{ backgroundColor: '#1e3a5f', color: 'white', padding: '10px 16px', borderRadius: '6px 6px 0 0' }}>
                  <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: 0 }}>Part V — Net Profit or Loss</h2>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', width: '60px', color: '#64748b', fontWeight: '500' }}>29</td>
                      <td style={{ padding: '10px 0' }}>Tentative profit (line 7 minus line 28)</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(scheduleC?.line_29_tentative_profit)}
                      </td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                      <td style={{ padding: '10px 16px', color: '#64748b', fontWeight: '500' }}>30</td>
                      <td style={{ padding: '10px 0' }}>Expenses for business use of home (Form 8829)</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                        {formatCurrency(scheduleC?.line_30_home_office_deduction)}
                      </td>
                    </tr>
                    <tr style={{ backgroundColor: scheduleC?.line_31_net_profit_or_loss >= 0 ? '#f0fdf4' : '#fef2f2', borderBottom: '3px solid #1e3a5f' }}>
                      <td style={{ padding: '14px 16px', color: '#1e3a5f', fontWeight: 'bold', fontSize: '14px' }}>31</td>
                      <td style={{ padding: '14px 0', fontWeight: 'bold', color: '#1e3a5f', fontSize: '14px' }}>
                        Net profit or (loss) — Enter on Form 1040 Schedule 1
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: 'bold', fontSize: '18px', color: scheduleC?.line_31_net_profit_or_loss >= 0 ? '#059669' : '#dc2626' }}>
                        {formatCurrency(scheduleC?.line_31_net_profit_or_loss)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Self-Employment Tax Estimate */}
              {scheduleC?.self_employment_tax_info && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ backgroundColor: '#7c3aed', color: 'white', padding: '10px 16px', borderRadius: '6px 6px 0 0' }}>
                    <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: 0 }}>Self-Employment Tax Estimate (Schedule SE)</h2>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px 16px' }}>Net earnings subject to SE tax (92.35% of net profit)</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                          {formatCurrency(scheduleC.self_employment_tax_info.net_earnings_subject_to_se)}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px 16px' }}>Social Security portion (12.4% up to wage base)</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                          {formatCurrency(scheduleC.self_employment_tax_info.social_security_tax)}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px 16px' }}>Medicare portion (2.9%)</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500' }}>
                          {formatCurrency(scheduleC.self_employment_tax_info.medicare_tax)}
                        </td>
                      </tr>
                      <tr style={{ backgroundColor: '#f5f3ff', borderBottom: '2px solid #7c3aed' }}>
                        <td style={{ padding: '12px 16px', fontWeight: 'bold', color: '#7c3aed' }}>Total Self-Employment Tax</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold', fontSize: '15px', color: '#7c3aed' }}>
                          {formatCurrency(scheduleC.self_employment_tax_info.total_se_tax)}
                        </td>
                      </tr>
                      <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                        <td style={{ padding: '10px 16px' }}>Deductible portion (50% of SE tax)</td>
                        <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '500', color: '#059669' }}>
                          {formatCurrency(scheduleC.self_employment_tax_info.se_tax_deduction)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Quarterly Estimates */}
              {quarterly && quarterly.quarters && (
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ backgroundColor: '#0369a1', color: 'white', padding: '10px 16px', borderRadius: '6px 6px 0 0' }}>
                    <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: 0 }}>Quarterly Estimated Tax Payments</h2>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f1f5f9' }}>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Quarter</th>
                        <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: '600', color: '#475569' }}>Due Date</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#475569' }}>Federal</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#475569' }}>State (NJ)</th>
                        <th style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600', color: '#475569' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {quarterly.quarters.map((q, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0' }}>
                          <td style={{ padding: '10px 16px', fontWeight: '500' }}>{q.quarter}</td>
                          <td style={{ padding: '10px 16px', color: '#64748b' }}>{q.due_date}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right' }}>{formatCurrency(q.federal)}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right' }}>{formatCurrency(q.state)}</td>
                          <td style={{ padding: '10px 16px', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(q.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ backgroundColor: '#e0f2fe' }}>
                        <td colSpan="2" style={{ padding: '12px 16px', fontWeight: 'bold', color: '#0369a1' }}>Annual Total</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(quarterly.annual_federal)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold' }}>{formatCurrency(quarterly.annual_state)}</td>
                        <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 'bold', color: '#0369a1', fontSize: '15px' }}>{formatCurrency(quarterly.annual_total)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* Footer */}
              <div style={{ marginTop: '32px', paddingTop: '16px', borderTop: '2px solid #e2e8f0', textAlign: 'center' }}>
                <p style={{ color: '#9ca3af', fontSize: '11px', margin: '0 0 4px 0' }}>
                  This report is for informational and planning purposes only. It does not constitute tax advice.
                </p>
                <p style={{ color: '#9ca3af', fontSize: '11px', margin: 0 }}>
                  Please consult a qualified tax professional for actual tax filing and compliance.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaxReportsPage;
